<template>
    <content-card class="vuetify cost-summary cost-summary-card" data-testid="estimate-summary-content"
        :mode="'estimate'">
        <template #header>
            <div class="content-card-header">
                <span> {{ $translate('accountsSummaryAccountCard.estimate') }} </span>
                <span class="sub-account-info"> # {{ getSubAccountID() }}</span>
            </div>
        </template>
        <div class="estimate-summary-header">
            <div v-if="estimate.appointmentInfo" class="header-provider-location">
                {{ estimate.appointmentInfo.providerLocation }}
            </div>
            <div v-if="estimate.patientName">
                <div class="header-services-for fs-block">{{ $translate('labels.patientName') }} {{ estimate.patientName
                    }}</div>
            </div>
            <div v-if="estimate.accountNumber && $vuetify.display.lgAndUp" class="header-account-number"
                data-testid="estimate-summary-header-account-number">
                {{ accountNumberLabel + ': ' + formattedAccountNumber }}
            </div>
            <div v-else-if="estimate.accountNumber">
                <span v-if="estimate.accountNumber" data-testid="estimate-summary-header-account-number">{{
                    accountNumberLabel + ': '
                    }}<a @click=" $emit('viewaccountinfo')" class="header-account-number vuetify">{{
                        formattedAccountNumber }}</a></span>
            </div>
        </div>
        <div class="bill-summary-balance">
            <div v-if="paymentMade">
                <div class="balance-amount-due-label">{{ $translate('labels.estimatedRemainingCost') }}</div>
                <div data-testid="estimate-summary-estimated-cost-label" class="balance-amount-due">{{
                    $formatCurrency(Number(amountRemaining)) }}</div>
            </div>
            <div v-else>
                <div class="balance-amount-due-label">{{ $translate('labels.estimatedAmount') }}</div>
                <div data-testid="estimate-summary-estimated-cost-label" class="balance-amount-due">{{
                    $formatCurrency(estimate.amountDue) }}
                </div>
            </div>
            <div class="account-status" v-if="showRequestedPaymentAmount">
                <div class="status-chip vuetify">
                    <base-chip data-testid="estimate-summary-requested-payment-notice" color="#1867C0">{{
                        $translate('labels.estimateRequestedAmountNotice',
                            {
                                amount:
                                    $formatCurrency(Number(requestedRemaining)), DOS: $formatDate(estimate.dateOfService,
                                        'M/D/YYYY')
                            }) }}
                    </base-chip>
                    <span v-if="this.paymentPercentage > 0 && this.paymentPercentage < 100 &&
                        estimate.appointmentInfo.providerLocation">
                        <base-icon @click="prepaymentDisclaimerBottomSheet = true"
                            data-testid="pre-payment-disclaimer-alert-icon" icon="mdi-help-circle-outline"
                            color="#1867C0" class="custom-icon"></base-icon>
                    </span>
                </div>
            </div>
            <div v-if="isVoid" class="estimate-alert" data-testid="estimate-summary-void">
                <base-icon :size="$vuetify.display.lgAndUp ? 20 : 16" icon="mdi-alert-circle-outline"></base-icon>
                <span> {{ $translate('labels.estimateVoid') }} </span>
            </div>

            <div v-if="isPaymentPlan || isVoid" class="account-status">
                <status-chip :class="{ 'view-plan-offset': (shouldViewPlanLink) }"
                    :estimate="estimateModel"></status-chip>
                <div v-if="shouldViewPlanLink" @click="viewPlanLink()" class="payment-plan-link">{{
                    $translate('actions.view') }}</div>
            </div>
            <div class="account-status">
                <div v-if="showPaymentButton">
                    <base-btn id="estimate-account-balance-button" data-testid="estimate-summary-view-payment-options"
                        class="button-bigger-text v-button-primary custom-button" :x-large="$vuetify.display.lgAndUp"
                        tabindex="1" @click="$emit('pay-estimate')">{{ $translate('actions.viewPaymentOptions')
                        }}</base-btn>
                </div>
            </div>
        </div>


        <div v-if="estimateSummary" class="cost-summary-breakdown data-grid">
            <div class="data-grid">
                <div class="row" v-for="(value, label) in estimateSummary" :key="label">
                    <div class="col-left col-left-padding">
                        <base-tooltip location="top" :header="$translate(('labels.' + label))"
                            :tip="$translate(('glossary.' + label))">
                            <div class="label tipped">{{ $translate(('labels.' + label)) }}</div>
                        </base-tooltip>
                    </div>
                    <div class="col-right" data-testid="estimate-summary-cost-amount">{{
                        $formatCurrency(Number(value))
                        }}</div>
                </div>
            </div>
        </div>
        <base-divider class="divider"></base-divider>
        <div class="disclaimer">
            <div>
                <span class="disclaimer-header-estimate">
                    <span data-testid="estimate-disclaimer-header" class="u-bold">{{
                        $translate('labels.estimateSummaryCustomDisclaimerHeader') }}</span>
                    <base-icon @click="disclaimerBottomSheet = true" data-testid="estimate-disclaimer-alert-icon"
                        icon="mdi-help-circle-outline" color="#1867C0" class="custom-icon"></base-icon>
                </span>
                <span data-testid="estimate-disclaimer-content">{{ $translate('labels.estimateSummaryDisclaimer')
                    }}</span>
            </div>
        </div>
        <base-bottom-sheet v-model="disclaimerBottomSheet" scrollable>
            <div class="disclaimer-details" v-html="$translate('labels.estimateSummaryDisclaimerDetails')"></div>
        </base-bottom-sheet>
        <base-bottom-sheet v-model="prepaymentDisclaimerBottomSheet">
            <div class="disclaimer-header-pre-payment" v-html="$translate('labels.prePaymentDisclaimerHeader')"></div>
            <div class="disclaimer-details" v-html="$translate('labels.prePaymentDisclaimer', {
                provider: estimate.appointmentInfo.providerLocation,
                paymentPercentage: this.paymentPercentage,
            })"></div>
        </base-bottom-sheet>
    </content-card>
</template>

<script>

import ContentCard from './ContentCard.vue';
import SubAccountDetailsBalance from './SubAccountDetailsBalance.vue';
import BaseIcon from './BaseIcon.vue';
import BaseBtn from './BaseBtn.vue';
import BaseCard from './BaseCard.vue';
import BaseChip from './BaseChip.vue';
import StatusChip from './StatusChip.vue';
import Estimate from './../models/Estimate';
import BaseDivider from './BaseDivider.vue';
import BaseBottomSheet from './BaseBottomSheet.vue';
import BaseTooltip from './BaseTooltip.vue';

export default {
    name: 'EstimateSummary',
    components: {
        ContentCard,
        SubAccountDetailsBalance,
        BaseIcon,
        BaseBtn,
        BaseCard,
        StatusChip,
        BaseDivider,
        BaseChip,
        BaseBottomSheet,
        BaseTooltip
    },
    props: {
        estimate: {
            type: Object,
            required: true,
        },
    },
    data: () => ({
        estimateSummary: {
        },
        disclaimerBottomSheet: false,
        prepaymentDisclaimerBottomSheet: false,

    }),
    computed: {
        currentEstimate() {
            return this.$store.getters.currentEstimate;
        },
        accountNumberLabel() {
            return this.$store.getters.accountNumberLabel;
        },
        formattedAccountNumber() {
            const maxLen = this.$vuetify.display.lgAndUp ? 25 : 15;
            if (this.estimate.accountNumber && this.estimate.accountNumber.length <= maxLen) {
                return this.estimate.accountNumber;
            } else if (this.estimate.accountNumber) {
                return `${this.estimate.accountNumber.substring(0, maxLen)}...`;
            }
            return null;
        },
        paymentMade() {
            return this.estimate.amountPaid > 0;
        },
        isVoid() {
            return this.estimate.cancelled;
        },
        isPaymentPlan() {
            return this.estimate.subAccountStatus == "PAYMENT_PLAN";
        },
        estimateModel() {
            return new Estimate(this.estimate);
        },
        shouldViewPlanLink() {
            return this.isPaymentPlan &&
                !this.isVoid &&
                this.estimate &&
                this.estimate.providerDetails &&
                this.estimate.providerDetails.id &&
                this.estimate.currentPlan &&
                this.estimate.currentPlan.paymentPlanId;
        },
        noPayments() {
            return this.estimate.amountPaid == 0 || this.estimate.amountPaid == null;
        },
        amountRemaining() {
            if (this.estimate.amountPaid == 0 || this.estimate.amountPaid == null) {
                return this.estimate.estimatedAmount;
            } else {
                return this.estimate.estimatedAmount - this.estimate.amountPaid;
            }
        },
        showRequestedPaymentAmount() {
            return this.estimate.requestedPaymentAmount > 0 && this.estimate.amountPaid < this.estimate.requestedPaymentAmount && this.isPayableStatus && !this.isPaymentPlan && !this.isVoid;
        },
        showPaymentButton() {
            return this.isPayableStatus && (this.estimate.amountDue > 0) && !this.estimate.cancelled;
        },
        isPayableStatus() {
            // not payable statuses are ADJUDICATED and INACTIVE
            let statuses = ["CREATED", "PRESERVICE_UNFULFILLED", "PRESERVICE_PARTIAL_FULFILLED", "PRESERVICE_FULFILLED", "PAYMENT_PLAN"];
            return statuses.includes(this.estimate.subAccountStatus);
        },
        requestedRemaining() {
            if (this.estimate.amountPaid == 0 || this.estimate.amountPaid == null) {
                return this.estimate.requestedPaymentAmount;
            } else {
                return this.estimate.requestedPaymentAmount - this.estimate.amountPaid;
                // can return a negative number but won't display because of showRequestedPaymentAmount
            }
        },
        paymentPercentage() {
            return this.estimate.requestedPaymentAmount / this.estimate.estimatedAmount * 100;
        },
    },
    methods: {
        viewPlanLink() {
            this.$router.push({
                name: 'PlanReceipt',
                params: {
                    providerId: this.estimate.providerDetails.id,
                    planId: this.estimate.currentPlan.paymentPlanId,
                },
            });
        },
        getSubAccountID() {
            return this.estimate.subAccountNumber;
        },
        calculateEOB() {
            if (this.estimate.serviceAmount != null && this.estimate.serviceAmount > 0) {
                this.estimateSummary.totalCharges = this.estimate.serviceAmount;
            }
            if (this.estimate.allowedAmount != null && this.estimate.allowedAmount > 0) {
                this.estimateSummary.adjustments = -(this.estimate.allowedAmount);
            }
            if (this.estimate.insuranceAmount != null && this.estimate.insuranceAmount > 0) {
                this.estimateSummary.insuranceResponsibility = -(this.estimate.insuranceAmount);
            }
            if (this.estimate.amountPaid != null && this.estimate.amountPaid > 0) {
                this.estimateSummary.patientPayments = -(this.estimate.amountPaid);
            }
            if (this.estimate.estimatedAmount != null && this.estimate.estimatedAmount > 0) {
                this.estimateSummary.patientResponsibility = this.estimate.estimatedAmount;
            }
        }
    },
    mounted() {
        this.calculateEOB();
    },
    watch: {
        estimate: {
            handler() {
                this.calculateEOB();
            },
            immediate: true,
            deep: true
        }
    }
};
</script>

<style lang="scss">
@import './../styles/variables.scss';
@import './../styles/balance.scss';
@import '../styles/cost-summary-shared.scss';

.disclaimer-details {
    padding-top: 2.5rem;
    font-size: 1.5rem;
    line-height: 2.4rem;
}

.disclaimer-header-pre-payment {
    font-size: 1.8rem;
    font-weight: 600;
    padding: 1rem 0;
    font-style: normal;
    text-align: left;
}

.disclaimer {
    font-size: 1.4rem;
    padding: 1rem;
    color: dimgrey;
    border-radius: .5rem;

    .disclaimer-header-estimate {
        display: flex;

        .custom-icon {
            margin-left: 0.5rem;
            cursor: pointer;
        }
    }
}

.custom-button {
    width: 384px;
    height: 53px;
}
</style>